import * as yup from 'yup'
import { addDays, addWeeks, addYears, format, isValid, parse, setMinutes, setHours } from 'date-fns'
import {
  HistoryDate,
  HistoryDateRequest,
  Observation
} from '@/Network/Types/Requests/StationRequests'
import type { StationPeriodicity } from '@/Network/Types/Requests/StationRequests'
import { UserInfo } from '@/Network/Types/Responses/UserResponse'
import {
  ExceptionSummaryStatus,
  StationSectorRef,
  StationSpecialtyRef
} from '@/Network/Types/Responses/StationResponses'
import type { ShiftDetails } from '@/Network/Types/Responses/StationResponses'
import { CoverageStatus } from '@/Network/Types/Responses/CoverageResponses'
import { CoverageMode } from '@/Network/Types/Requests/CoverageRequests'
import type { CoverageInput } from '@/Network/Types/Requests/CoverageRequests'
import { getStationCoverageInfo } from '@/utils/coverage'
import { parseLocalDate, toDateOnly } from '@/utils/date'
import type { SpecialtySelection } from '@/utils/sectorSpecialty'
import { recurrenceDescriptions, weekMapping } from '../utils/data'

export interface StationToUpdate {
  stationId: number
  companyId: number
  doctorId?: number
  date: Date
  shift?: {
    id: number
    name: string
    deletedAt?: Date | null
    createdAt?: Date
    updatedAt?: Date | null
    isActive?: boolean
  }
  startHour?: Date
  endHour?: Date
  timezone: string
  stationType: string
  ourTeam: boolean
  inCash: boolean
  advanceRequested: boolean
  advancePaid: boolean
  modifiedValue?: boolean
  value: number
  checks: HistoryDate[]
  observations?: Observation[]
  doctorPending?: boolean
  urlConviteDoctor?: string
  stationValue: number
  position: any
  healthUnitLatitude: string
  healthUnitLongitude: string
  doctorStationId?: number
  userSponsor?: UserInfo
  scheduleId?: number
  /** Setor/especialidade gravados no plantão (P1). Opcionais: BE anterior à F3 não manda. */
  sector?: StationSectorRef | null
  specialty?: StationSpecialtyRef | null
  schedule?: any
  doctorStation?: {
    id: number
    dsPaymentDoctorFormation: {
      id: number
      name?: string
      deletedAt?: Date | null
      createdAt?: Date
      updatedAt?: Date | null
      isActive?: boolean
    }
    doctor: {
      id?: number
      mainCrm: {
        id: number
        name: string
        status: string
        number: string
        uf: string
        createdAt: Date
        updatedAt: Date | null
        deletedAt: Date | null
        isActive: boolean
      }
      user?: { id: number; name: string; avatarUrl?: string | null }
    }
    type: string
    ourTeam: boolean
    requestAntecipe: boolean
    antecipeWasPaid: boolean
  }
  exceptionStatus?: ExceptionSummaryStatus
  doctorNpsScore?: {
    npsScore: number
    avgScore: number
    evaluations: number
    profile: string
  } | null
  /**
   * F2 — bloqueio do lançamento manual de check, repassado do campo aditivo de
   * `GET /stations/:id`. Ausente (backend anterior à F2), a aba de checks cai no
   * fallback por `registration-settings` do `useDsAttendanceStore`.
   */
  manualCheckBlockEnabled?: boolean
  /** Estado da cobertura (aditivo): ausente com backend anterior à feature. */
  coverageStatus?: CoverageStatus
  coverageResponsibleId?: number | null
  coverageResponsibleName?: string | null
  coverageNote?: string | null
}

/**
 * Pré-preenchimento do "Criar plantão" vindo de outra tela (Timeline no celular: "Criar vaga
 * HH–HH" no buraco). Datas e horas no formato dos campos do formulário. `companyId`/`scheduleId`
 * nulos = o usuário escolhe; a especialidade nunca vem (o usuário escolhe).
 */
export interface CreateStationPrefill {
  companyId: number | null
  healthUnitId: number
  scheduleId: number | null
  /** `dd/MM/yyyy`. */
  date: string
  /** `dd/MM/yyyy`. */
  endDate: string
  /** `HH:mm`. */
  startHour: string
  /** `HH:mm`. */
  endHour: string
  sector: StationSectorRef | null
}

export const optionsRepeatStation = [
  { title: 'Domingo', value: '0' },
  { title: 'Segunda-feira', value: '1' },
  { title: 'Terça-feira', value: '2' },
  { title: 'Quarta-feira', value: '3' },
  { title: 'Quinta-feira', value: '4' },
  { title: 'Sexta-feira', value: '5' },
  { title: 'Sábado', value: '6' }
]

// Regras de repetição (término, tetos, periodicidade) e o teto de 2 anos do 'always', que para
// em min(fim da escala, início + 2 anos): bng-health/minhaescala-backend#3094. Ler antes de mudar
// e atualizar se mudar.
export const finishedAtOptions = [
  { title: 'Não repetir', value: 'never' },
  { title: 'Até o fim da escala (máx. 2 anos)', value: 'always' },
  { title: 'Até', value: 'until' },
  { title: 'Após', value: 'occurrence' }
]

export const periodicityOptions: Array<{
  title: string
  value: StationPeriodicity
  description: string
}> = [
  { title: 'Semanal', value: 'WEEKLY', description: recurrenceDescriptions.weekly },
  { title: 'Quinzenal', value: 'BIWEEKLY', description: recurrenceDescriptions.fortnightly },
  { title: 'Mensal ABCDE', value: 'MONTHLY_ABCDE', description: recurrenceDescriptions.abcde }
]

export type RecurrenceSelection = {
  configurationRepetition: string
  periodicity: StationPeriodicity
  selectedWeeks: string[]
}

export type RecurrencePayload = {
  periodicity?: StationPeriodicity
  weeksOfMonth?: number[]
}

export const buildRecurrencePayload = ({
  configurationRepetition,
  periodicity,
  selectedWeeks
}: RecurrenceSelection): RecurrencePayload => {
  if (configurationRepetition === 'no-repeat') return {}
  if (periodicity !== 'MONTHLY_ABCDE') return { periodicity }

  // Validação defensiva: descartar valores inválidos de semanas
  const validWeeks = selectedWeeks
    .filter((week) => weekMapping[week] !== undefined)
    .map((week) => weekMapping[week])
    .sort((a, b) => a - b)

  return {
    periodicity,
    weeksOfMonth: validWeeks
  }
}

// "Não repetir" usa horizonte de 1 semana no backend, o que geraria 0 ou 1 vaga fora do semanal.
export const getFinishedAtOptionsFor = (periodicity: StationPeriodicity) =>
  periodicity === 'WEEKLY'
    ? finishedAtOptions
    : finishedAtOptions.filter((option) => option.value !== 'never')

/**
 * Término a aplicar ao trocar a periodicidade, ou null se o atual continua válido.
 * "Não repetir" some fora do semanal e vira "Após 1" (1 quinzena / 1 mês): o mais perto
 * do que o usuário escolheu. Nunca "Até o fim da escala", que pode gerar até 2 anos (#3094).
 */
export const getFinishedAtOnPeriodicityChange = (
  periodicity: StationPeriodicity,
  finishedAt: string
): { finishedAt: 'occurrence'; finishedAtAfter: number } | null =>
  periodicity !== 'WEEKLY' && finishedAt === 'never'
    ? { finishedAt: 'occurrence', finishedAtAfter: 1 }
    : null

/** "Após N" conta períodos da periodicidade (semanas, quinzenas ou meses), não plantões. */
export const getOccurrenceUnitFor = (periodicity: StationPeriodicity) => {
  if (periodicity === 'BIWEEKLY') return { placeholder: 'Nº de quinzenas', suffix: 'Quinzenas' }
  if (periodicity === 'MONTHLY_ABCDE') return { placeholder: 'Nº de meses', suffix: 'Meses' }
  return { placeholder: 'Nº de semanas', suffix: 'Semanas' }
}

/**
 * Teto de "Após N" por periodicidade. Espelho de PERIODICITY_RULES do backend
 * (src/modules/stations/domain/utils/station-recurrence.utils.ts) — mudar os dois juntos.
 * Independe dos 2 anos do 'always': ver bng-health/minhaescala-backend#3094.
 */
export const OCCURRENCE_LIMITS: Record<
  StationPeriodicity,
  { max: number; name: string; unit: string }
> = {
  WEEKLY: { max: 104, name: 'semanal', unit: 'semanas' },
  BIWEEKLY: { max: 52, name: 'quinzenal', unit: 'quinzenas' },
  MONTHLY_ABCDE: { max: 24, name: 'mensal (A a E)', unit: 'meses' }
}

/** Teto, nome e unidade da periodicidade; ausente (ou desconhecida em runtime) cai no semanal. */
export const getOccurrenceLimitFor = (periodicity: StationPeriodicity | undefined) =>
  (periodicity && OCCURRENCE_LIMITS[periodicity]) || OCCURRENCE_LIMITS.WEEKLY

/**
 * Erro de "Após N" fora da faixa aceita pelo backend (inteiro de 1 até o teto da
 * periodicidade); senão, undefined. Campo vazio chega aqui como 0.
 */
export const getOccurrenceLimitError = (
  periodicity: StationPeriodicity | undefined,
  finishedAt: string,
  value: number
): string | undefined => {
  if (finishedAt !== 'occurrence') return undefined
  const { max, name, unit } = getOccurrenceLimitFor(periodicity)
  if (!Number.isInteger(value) || value < 1)
    return `Informe um número inteiro de ${unit}, a partir de 1.`
  if (value <= max) return undefined
  return `Para recorrência ${name}, o máximo é ${max} ${unit}.`
}

export const StationSchema = yup.object({
  // SCHEDULE SECTION
  company: yup.number().integer().nullable(),
  healthUnit: yup.number().integer().nullable(),
  schedule: yup.number().integer().nullable(),
  userSponsorId: yup.number().integer().optional(), // Old: sponsorDoctorId
  // DATE SECTION
  date: yup.string().required(),
  startHour: yup.string().required(),
  endHour: yup.string().required(),
  timezone: yup.string().required(),
  configurationRepetion: yup.string().oneOf(['no-repeat', 'daily', 'custom']).required(),
  valueRepeation: yup.array().when('configurationRepetion', {
    is: 'custom',
    then: yup.array().min(1).required(),
    otherwise: yup.number().nullable().optional()
  }),
  finishedAt: yup.string().oneOf(['never', 'until', 'occurrence', 'always']).required(),
  finishedAtUntil: yup.date().optional(),
  finishedAtAfter: yup.number().when('finishedAt', {
    is: 'occurrence',
    then: yup.number().integer().min(1).required(),
    otherwise: yup.number().nullable().optional()
  }),
  // INFO SECTION
  ourTeam: yup.boolean().required(),
  stationType: yup.string().required(),

  // VALUES SECTION
  doctorId: yup.number().integer().optional(),
  inCash: yup.boolean().required(),
  advanceRequested: yup.boolean().required(),
  advancePaid: yup.boolean().required(),
  urlConviteDoctor: yup.string().optional(),
  value: yup.number().optional(),

  // OBSERVATION SECTION
  observations: yup
    .array()
    .of(
      yup.object({
        date: yup.string().optional(),
        description: yup.string().min(1).required(),
        userName: yup.string().optional()
      })
    )
    .required()
})

export type Station = yup.InferType<typeof StationSchema>
export type ErrorStation = { [K in keyof Station]: string }

export const defaultValues = {
  company: null,
  userSponsorId: undefined, // Old: sponsorDoctorId
  doctorId: undefined,
  startHour: format(new Date(), 'HH:mm'),
  endHour: format(new Date(), 'HH:mm'),
  timezone: '-03:00',
  date: format(new Date(), 'dd/MM/yyyy'),
  configurationRepetion: 'no-repeat',
  finishedAt: 'never',
  finishedAtAfter: undefined,
  finishedAtUntil: new Date(),
  healthUnit: null,
  ourTeam: true,
  value: undefined,
  schedule: undefined,
  stationType: 'FIXO',
  valueRepeation: undefined,
  inCash: false,
  advanceRequested: false,
  urlConviteDoctor: '#',
  advancePaid: false,
  observations: []
}
const values: Station = { ...defaultValues }

export const states = {
  values
}

export const getDate = ({
  date,
  hours,
  timezone = '-03:00'
}: {
  date: string
  hours: string
  timezone?: string
}): Date => {
  const [day, month, year] = date.split('/')
  const [hour, minute] = hours.split(':')
  const formatedDate = `${year}-${month}-${day}T${hour}:${minute}:00.000${timezone}`
  return new Date(formatedDate)
}

export const getStartDateAndEndDate = ({
  date,
  endDateSelected,
  startHour,
  endHour,
  timezone = '-03:00'
}: {
  date: string
  endDateSelected: string
  startHour: string
  endHour: string
  timezone?: string
}): { startDate: Date; endDate: Date } => {
  const startDate = getDate({ date, hours: startHour, timezone })
  const endDate = getDate({ date: endDateSelected, hours: endHour, timezone })

  return { startDate, endDate }
}

export const hourDate = (data: Date, text: string) => {
  const hour = Number(text.split(':')[0])
  const minutes = Number(text.split(':')[1])
  const res = setMinutes(setHours(data, hour), minutes)
  return res
}

export const getHistoriesRequest = (
  data: HistoryDate[],
  startDate: Date,
  endDate: Date
): HistoryDateRequest[] => {
  const payload = data.map((c): HistoryDateRequest => {
    let checkin = c.checkin
    let checkout = c.checkout
    if (c.checkin.split('T').length > 1) {
      const hours = new Date(c.checkin).getHours()
      const minutes = new Date(c.checkin).getMinutes()
      checkin = `${hours}:${minutes}`
    }

    if (c.checkout && c.checkout.split('T').length > 1) {
      const hours = new Date(c.checkout).getHours()
      const minutes = new Date(c.checkout).getMinutes()
      checkout = `${hours}:${minutes}`
    }

    // `isMain` deixou de viajar no request (BF5): o servidor decide o check
    // principal em `ensureMainCheck` e ignora o campo — mandá-lo sugeriria um
    // controle que a UI não tem mais.
    const historyRequest: HistoryDateRequest = {
      id: c.id,
      checkin: hourDate(startDate, checkin),
      checkout: checkout ? hourDate(endDate, checkout) : undefined,
      origin: c.origin
    }

    if (c.checkEvidences && c.checkEvidences.length > 0) {
      historyRequest.checkEvidences = c.checkEvidences
    }

    return historyRequest
  })

  return payload
}

type RecurrenceParams = {
  date: string
  finishedAt: string
  finishedAtAfter: number
  finishedAtUntil: string
  repetionDays: Record<string, string>
  periodicity?: StationPeriodicity
  /** `endDate` da escala como vem do GET (ISO); `null`/ausente = escala sem fim. */
  scheduleEndDate?: string | null
}

const parseBrDate = (value: string): Date | null => {
  if (!value) return null
  const parsed = parse(value, 'dd/MM/yyyy', new Date())
  return isValid(parsed) ? parsed : null
}

/** Parada pelo tipo de término, sem olhar o fim da escala; `null` quando não dá para saber. */
function getStopDateByFinishType(start: Date, params: RecurrenceParams): Date | null {
  switch (params.finishedAt) {
    case 'occurrence':
      // Fim exclusivo: N semanas a partir do início, sem o dia de início + N semanas.
      return addDays(addWeeks(start, Number(params.finishedAtAfter) || 1), -1)
    case 'always':
      // "Até o fim da escala (máx. 2 anos)": o fim da escala entra em getRecurrenceStopDate.
      return addYears(start, 2)
    case 'never':
      return addWeeks(start, 1)
    case 'until':
      return parseBrDate(params.finishedAtUntil)
    default:
      return null
  }
}

/**
 * Espelho de `computeStopDate` do BE (semanal): nenhum término passa do último dia da escala,
 * inclusive (bng-health/minhaescala-backend#3094). O dia vem da data do texto ISO, não do fuso
 * do navegador (meia-noite UTC viraria o dia anterior em BRT).
 */
function getRecurrenceStopDate(start: Date, params: RecurrenceParams): Date | null {
  const stop = getStopDateByFinishType(start, params)
  const lastDay = params.scheduleEndDate ? parseLocalDate(toDateOnly(params.scheduleEndDate)) : null
  return stop && lastDay && lastDay < stop ? lastDay : stop
}

/**
 * Estimativa do nº de plantões da repetição, espelhando o laço do BE
 * (`station-recurrence.utils.ts#generateRecurrenceDates` + `computeStopDate`: do dia inicial até o
 * dia de parada, inclusive; posições extras do dia somam). É teto: o BE ainda descarta
 * duplicados (Q31), por isso o texto diz "Até N".
 */
export function estimateRecurringStationCount(params: RecurrenceParams): number | null {
  // Laço só espelha o semanal; quinzenal/ABCDE caem no resumo sem contagem.
  if ((params.periodicity ?? 'WEEKLY') !== 'WEEKLY') return null
  const start = parseBrDate(params.date)
  if (!start) return null
  const stop = getRecurrenceStopDate(start, params)
  // Início depois do fim da escala: o BE responde 400, não há contagem a mostrar.
  if (!stop || stop < start) return null
  let total = 0
  for (let day = start; day.getTime() <= stop.getTime(); day = addDays(day, 1)) {
    const qty = Number(params.repetionDays[String(day.getDay())] ?? 0)
    if (qty > 0) total += qty
  }
  return total
}

export function buildSectorSpecialtySummary(
  count: number | null,
  sector: StationSectorRef | null,
  specialty: SpecialtySelection | null
): string {
  const sectorText = sector ? `com setor ${sector.name}` : 'sem setor'
  // "com setor X e especialidade Y" | "sem setor e com especialidade Y"
  const specialtyText = specialty
    ? `${sector ? '' : 'com '}especialidade ${specialty.name}`
    : 'sem especialidade'
  const both = `${sectorText} e ${specialtyText}`
  return count !== null
    ? `Até ${count} plantões serão criados ${both}.`
    : `Todos os plantões gerados pela repetição serão criados ${both}.`
}

type CoverageUpdateFields = Pick<
  StationToUpdate,
  'coverageStatus' | 'coverageResponsibleId' | 'coverageResponsibleName' | 'coverageNote'
>

/** Campos de cobertura do `GET /stations/:id` para o `StationToUpdate` — vazio com BE antigo. */
export function mapCoverageToStationUpdate(
  details: Pick<ShiftDetails, 'doctorStation'> | null | undefined
): CoverageUpdateFields {
  const info = getStationCoverageInfo(details)
  if (!info.hasContract) return {}
  return {
    coverageStatus: info.status,
    coverageResponsibleId: info.responsibleId,
    coverageResponsibleName: info.responsibleName,
    coverageNote: info.note
  }
}

type DetailOwnedUpdateFields = Pick<
  StationToUpdate,
  | 'userSponsor'
  | 'scheduleId'
  | 'inCash'
  | 'healthUnitLatitude'
  | 'healthUnitLongitude'
  | 'doctorNpsScore'
>

/**
 * Campos do "Editar plantão" que vêm do detalhe fresco (`GET /stations/:id`), e não de quem
 * abriu o detalhe. O `handleEditStation` espalha o `StationToUpdate` de quem abriu: o card da
 * grade traz estes campos, mas o deep link `?stationId=` (notificação do convite) e a barra da
 * Timeline só conhecem o `stationId` — e o "Editar" acusava "Informe por quem o médico foi
 * alocado." (sem `userSponsor`) e mandaria o PUT para `/schedule/NaN` (sem `scheduleId`).
 * O detalhe manda; o que ele não trouxer cai no valor de quem abriu.
 */
export function mapDetailOwnedFieldsToStationUpdate(
  details: Pick<ShiftDetails, 'doctorStation' | 'schedule' | 'doctorNpsScore'>,
  opened: Partial<StationToUpdate> | null | undefined
): DetailOwnedUpdateFields {
  return {
    userSponsor: details.doctorStation?.userSponsor ?? opened?.userSponsor,
    scheduleId: details.schedule?.id ?? opened?.scheduleId,
    inCash: details.doctorStation?.inCash ?? opened?.inCash ?? false,
    ...mapHealthUnitCoordinates(details, opened),
    doctorNpsScore: details.doctorNpsScore ?? opened?.doctorNpsScore ?? null
  }
}

/** Coordenadas da unidade (mapa dos checks): o tipo do detalhe não declara o `address`. */
function mapHealthUnitCoordinates(
  details: Pick<ShiftDetails, 'schedule'>,
  opened: Partial<StationToUpdate> | null | undefined
): Pick<StationToUpdate, 'healthUnitLatitude' | 'healthUnitLongitude'> {
  const address = (
    details.schedule?.healthUnit as { address?: { latitude?: string; longitude?: string } }
  )?.address
  return {
    healthUnitLatitude: address?.latitude ?? opened?.healthUnitLatitude ?? '',
    healthUnitLongitude: address?.longitude ?? opened?.healthUnitLongitude ?? ''
  }
}

/**
 * E-06 / Q31: a edição unitária passa a aceitar OPEN e UNIDENTIFIED (horário/valor).
 * Mandar o estado explícito evita que o backend leia "sem médico" como remoção.
 */
export function buildUpdateCoverageInput(
  infos: Pick<StationToUpdate, 'coverageStatus' | 'coverageResponsibleId' | 'coverageNote'>
): CoverageInput | null {
  if (infos.coverageStatus === CoverageStatus.UNIDENTIFIED) {
    return {
      coverage: CoverageMode.UNIDENTIFIED,
      coverageResponsibleId: infos.coverageResponsibleId ?? undefined,
      ...(infos.coverageNote ? { coverageNote: infos.coverageNote } : {})
    }
  }
  if (infos.coverageStatus === CoverageStatus.OPEN) return { coverage: CoverageMode.OPEN }
  return null
}
